import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTreeModule } from '@angular/material/tree';

// --- Interfaces ---
export interface ServiceNode {
  name: string;
  serviceId?: number;
  serviceGroupId?: number;
  description?: string;
  children?: ServiceNode[];
}

export interface ServiceFlatNode {
  expandable: boolean;
  name: string;
  level: number;
  serviceId?: number;
  serviceGroupId?: number;
}

@Component({
  selector: 'app-service-tree',
  templateUrl: './service-tree.component.html',
  styleUrls: ['./service-tree.component.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatTreeModule
]
})
export class ServiceTreeComponent implements OnInit, OnChanges {
  @Input() title = 'Affected Services';
  @Input() required = true;
  @Input() treeData: ServiceNode[] = [];
  @Input() showValidationError = false;
  @Input() showEmptyState = false;
  @Input() readonly = false;
  @Output() selectionChanged = new EventEmitter<SelectionModel<ServiceFlatNode>>();
  @Output() touched = new EventEmitter<void>();

  // Modern Tree Configuration (Data-driven)
  levelAccessor = (node: ServiceFlatNode) => node.level;
  
  // Flattened data array used as the data source
  flatData: ServiceFlatNode[] = [];
  
  // SelectionModel for tracking expanded nodes (replacement for treeControl expansion)
  expansionModel = new SelectionModel<ServiceFlatNode>(true);
  
  checklistSelection = new SelectionModel<ServiceFlatNode>(true /* multiple */);
  nestedNodeMap = new Map<ServiceNode, ServiceFlatNode>();
  searchQuery = '';
  private filteredNodeSet = new Set<ServiceFlatNode>();

  /** 
   * Filtered version of the flat data for rendering.
   * Ensures that nodes are only shown if all their parent nodes are expanded.
   */
  get visibleNodes(): ServiceFlatNode[] {
    if (this.hasActiveSearch) {
      return this.flatData.filter((node) => this.filteredNodeSet.has(node));
    }

    const result: ServiceFlatNode[] = [];
    let hiddenLevel = Infinity;

    for (const node of this.flatData) {
      if (node.level <= hiddenLevel) {
        hiddenLevel = Infinity;
        result.push(node);
        if (node.expandable && !this.isExpanded(node)) {
          hiddenLevel = node.level;
        }
      }
    }
    return result;
  }

  get hasActiveSearch(): boolean {
    return this.searchQuery.trim().length > 0;
  }

  get hasSearchResults(): boolean {
    return this.visibleNodes.length > 0;
  }

  get leafServiceNodes(): ServiceFlatNode[] {
    return this.flatData.filter((node) => !node.expandable && node.serviceId !== undefined);
  }

  get selectAllActionLabel(): string {
    if (this.hasActiveSearch) {
      return this.areAllScopedLeafServicesSelected() ? 'Clear Filtered' : 'Select Filtered';
    }
    return this.areAllLeafServicesSelected() ? 'Clear All' : 'Select All';
  }

  get isSelectAllActionActive(): boolean {
    return this.hasActiveSearch
      ? this.areAllScopedLeafServicesSelected()
      : this.areAllLeafServicesSelected();
  }

  get isSelectAllActionDisabled(): boolean {
    return this.readonly || this.getSelectableLeafServiceNodes().length === 0;
  }

  /**
   * Indicates whether root-level service groups exist.
   * Used to reserve toggle-slot spacing only when group rows are present.
   */
  get hasTopLevelGroups(): boolean {
    return this.flatData.some((node) => node.level === 0 && node.expandable);
  }

  /** 
   * Compatibility shim for external callers (e.g. status-page components) 
   * that still reference .treeControl.dataNodes.
   * @deprecated Use .flatData directly instead.
   */
  get treeControl() {
    return {
      dataNodes: this.flatData
    };
  }

  private _touched = false;

  ngOnInit() {
    this.updateDataSource(this.treeData);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.treeData) {
      this.updateDataSource(changes.treeData.currentValue || []);
    }
  }

  hasChild = (_: number, node: ServiceFlatNode) => node.expandable;

  // --- Expansion Logic ---

  isExpanded(node: ServiceFlatNode): boolean {
    return this.expansionModel.isSelected(node);
  }

  toggleNode(node: ServiceFlatNode): void {
    this.expansionModel.toggle(node);
  }

  collapseAll(): void {
    this.expansionModel.clear();
  }

  /**
   * Replaces treeControl.getDescendants(node)
   * Returns all descendants (children, grandchildren, etc) of the given flat node.
   */
  getDescendants(node: ServiceFlatNode): ServiceFlatNode[] {
    const result: ServiceFlatNode[] = [];
    const startIndex = this.flatData.indexOf(node);
    
    if (startIndex === -1) return result;

    for (let i = startIndex + 1; i < this.flatData.length && this.flatData[i].level > node.level; i++) {
      result.push(this.flatData[i]);
    }
    return result;
  }

  // --- Selection Logic ---

  /** Toggle a leaf node selection. Check all parents to see if they need to change */
  todoLeafItemSelectionToggle(node: ServiceFlatNode): void {
    if (this.readonly) {
      return;
    }
    this.markAsTouched();
    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
    this.selectionChanged.emit(this.checklistSelection);
  }

  onLeafCheckboxEnter(event: KeyboardEvent, node: ServiceFlatNode): void {
    event.preventDefault();
    this.todoLeafItemSelectionToggle(node);
  }

  /** Toggle a parent node selection. Select/Deselect all descendants */
  todoItemSelectionToggle(node: ServiceFlatNode): void {
    if (this.readonly) {
      return;
    }
    this.markAsTouched();
    this.checklistSelection.toggle(node);
    const descendants = this.getDescendants(node);
    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants);
    this.selectionChanged.emit(this.checklistSelection);
  }

  onParentCheckboxEnter(event: KeyboardEvent, node: ServiceFlatNode): void {
    event.preventDefault();
    this.todoItemSelectionToggle(node);
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.searchQuery = input?.value || '';
    this.applySearchFilter();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.filteredNodeSet.clear();
  }

  areAllLeafServicesSelected(): boolean {
    const leafNodes = this.leafServiceNodes;
    return leafNodes.length > 0 && leafNodes.every((node) => this.checklistSelection.isSelected(node));
  }

  areAllScopedLeafServicesSelected(): boolean {
    const scopedLeafNodes = this.getSelectableLeafServiceNodes();
    return scopedLeafNodes.length > 0 && scopedLeafNodes.every((node) => this.checklistSelection.isSelected(node));
  }

  toggleSelectAllServices(): void {
    const scopedLeafNodes = this.getSelectableLeafServiceNodes();
    if (this.readonly || scopedLeafNodes.length === 0) {
      return;
    }

    this.markAsTouched();

    if (scopedLeafNodes.every((node) => this.checklistSelection.isSelected(node))) {
      this.checklistSelection.deselect(...scopedLeafNodes);
    } else {
      this.checklistSelection.select(...scopedLeafNodes);
    }

    this.syncParentSelectionStates();
    this.selectionChanged.emit(this.checklistSelection);
  }

  descendantsPartiallySelected(node: ServiceFlatNode): boolean {
    const descendants = this.getDescendants(node);
    const result = descendants.some(child => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  descendantsAllSelected(node: ServiceFlatNode): boolean {
    const descendants = this.getDescendants(node);
    const descAllSelected = descendants.length > 0 && descendants.every(child => {
      return this.checklistSelection.isSelected(child);
    });
    return descAllSelected;
  }

  checkAllParentsSelection(node: ServiceFlatNode): void {
    let parent: ServiceFlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  checkRootNodeSelection(node: ServiceFlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.getDescendants(node);
    const descAllSelected = descendants.length > 0 && descendants.every(child => {
      return this.checklistSelection.isSelected(child);
    });
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  getParentNode(node: ServiceFlatNode): ServiceFlatNode | null {
    const currentLevel = node.level;
    if (currentLevel < 1) { return null; }
    const startIndex = this.flatData.indexOf(node) - 1;
    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.flatData[i];
      if (currentNode.level < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  // --- Helper Methods ---

  getSelectionStatus(node: ServiceFlatNode): string {
    if (!node.expandable) return '';
    const descendants = this.getDescendants(node);
    const total = descendants.length;
    const selected = descendants.filter(d => this.checklistSelection.isSelected(d)).length;
    return `(${selected}/${total} services)`;
  }

  // --- Public API ---

  isEmpty(): boolean {
    return this.checklistSelection.isEmpty();
  }

  markAsTouched(): void {
    if (!this._touched) {
      this._touched = true;
      this.touched.emit();
    }
  }

  isTouched(): boolean {
    return this._touched;
  }

  getFormattedServices(): any[] {
    const result: any[] = [];
    const rootNodes = this.treeData;

    rootNodes.forEach(rootNode => {
      const flatNode = this.nestedNodeMap.get(rootNode);

      if (flatNode) {
        const childPayloads: any[] = [];
        if (rootNode.children && rootNode.children.length > 0) {
          rootNode.children.forEach(child => {
            const childFlatNode = this.nestedNodeMap.get(child);
            const selected = childFlatNode ? this.checklistSelection.isSelected(childFlatNode) : false;
            if (selected) {
              childPayloads.push({
                id: child.serviceId,
                name: child.name,
                selected: true
              });
            }
          });
        }

        if (childPayloads.length > 0) {
          result.push({
            name: rootNode.name,
            selected: true,
            children: childPayloads,
            id: rootNode.serviceGroupId
          });
        }
      }
    });

    return result;
  }

  getSelectedServiceIds(): number[] {
    const ids = new Set<number>();
    this.flatData.forEach(node => {
      if (!node.expandable && this.checklistSelection.isSelected(node) && node.serviceId !== undefined) {
        ids.add(node.serviceId);
      }
    });
    return Array.from(ids);
  }

  setSelectedServiceIds(serviceIds: number[]): void {
    const idSet = new Set(serviceIds || []);
    this.checklistSelection.clear();
    this.flatData.forEach(node => {
      if (!node.expandable && node.serviceId !== undefined && idSet.has(node.serviceId)) {
        this.checklistSelection.select(node);
      }
    });
    this.flatData
      .filter(node => node.expandable)
      .forEach(node => this.checkRootNodeSelection(node));
    this.selectionChanged.emit(this.checklistSelection);
  }

  private updateDataSource(data: ServiceNode[]): void {
    this.nestedNodeMap.clear();
    this.checklistSelection.clear();
    this.collapseAll();
    
    // Perform manual flattening of nested data
    this.flatData = [];
    this._flattenNodes(data, 0, this.flatData);
    this.applySearchFilter();
  }

  private _flattenNodes(nodes: ServiceNode[], level: number, result: ServiceFlatNode[]): void {
    if (!nodes) return;
    
    nodes.forEach(node => {
      const flatNode: ServiceFlatNode = {
        name: node.name,
        serviceId: node.serviceId,
        serviceGroupId: node.serviceGroupId,
        level: level,
        expandable: !!node.children && node.children.length > 0
      };
      
      this.nestedNodeMap.set(node, flatNode);
      result.push(flatNode);
      
      if (flatNode.expandable && node.children) {
        this._flattenNodes(node.children, level + 1, result);
      }
    });
  }

  private applySearchFilter(): void {
    this.filteredNodeSet.clear();

    const normalizedSearch = this.searchQuery.trim().toLowerCase();
    if (!normalizedSearch) {
      return;
    }

    this.treeData.forEach((node) => {
      this.collectFilteredNodes(node, normalizedSearch, false);
    });
  }

  private collectFilteredNodes(node: ServiceNode, normalizedSearch: string, ancestorMatched: boolean): boolean {
    const nodeName = String(node?.name || '').trim().toLowerCase();
    const selfMatched = nodeName.includes(normalizedSearch);
    const includeAllDescendants = ancestorMatched || selfMatched;
    let descendantMatched = false;

    for (const child of node.children || []) {
      if (this.collectFilteredNodes(child, normalizedSearch, includeAllDescendants)) {
        descendantMatched = true;
      }
    }

    const includeCurrentNode = includeAllDescendants || descendantMatched;
    if (includeCurrentNode) {
      const flatNode = this.nestedNodeMap.get(node);
      if (flatNode) {
        this.filteredNodeSet.add(flatNode);
      }
    }

    return includeCurrentNode;
  }

  private getSelectableLeafServiceNodes(): ServiceFlatNode[] {
    if (this.hasActiveSearch) {
      return this.visibleNodes.filter((node) => !node.expandable && node.serviceId !== undefined);
    }
    return this.leafServiceNodes;
  }

  private syncParentSelectionStates(): void {
    const expandableNodes = this.flatData.filter((node) => node.expandable);
    for (let i = expandableNodes.length - 1; i >= 0; i--) {
      this.checkRootNodeSelection(expandableNodes[i]);
    }
  }
}
